import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios from "../utils/axios";

// Types (these should be defined in your types folder, but included here for completeness)
export interface AssetDto {
  id: string;
  name: string;
  symbol: string;
  assetType: number;
  assetDetails: string;
  totalSupply: number;
  decimals: number;
  status: AssetStatus;
  userId: number;
  contractAddress?: string;
  isVerified: boolean;
  verificationDate?: string;
  creationTime: string;
}

export interface CreateAssetDto {
  name: string;
  symbol: string;
  assetType: string;
  assetDetails: string;
  totalSupply: number;
  decimals: number;
}

export interface AssetBalanceDto {
  id: string;
  balance: number;
  assetId: string;
  userId: number;
  asset: AssetDto;
}

export interface DeployContractResultDto {
  transactionHash: string;
  contractAddress: string;
  success: boolean;
  error?: string;
}

export interface PagedAssetResultRequestDto {
  skipCount?: number;
  maxResultCount?: number;
  keyword?: string;
  assetType?: string;
  status?: AssetStatus;
}

export interface PagedResultDto<T> {
  totalCount: number;
  items: T[];
}

export enum AssetStatus {
    Draft = 1,
Minting = 2,
Deployed = 3,
Verified = 4,
Active = 5,
Failed = 6,
Suspended = 7
//   Draft = 0,
//   Minting = 1,
//   Verified = 2,
//   Active = 3,
//   Suspended = 4
}

export interface AssetsState {
  assets: AssetDto[];
  userAssets: AssetDto[];
  assetBalances: AssetBalanceDto[];
  currentAsset: AssetDto | null;
  loading: boolean;
  error: string | null;
  createLoading: boolean;
  createError: string | null;
  deployLoading: boolean;
  deployError: string | null;
  verifyLoading: boolean;
  verifyError: string | null;
  getAllLoading: boolean;
  getAllError: string | null;
  pagination: {
    totalCount: number;
    skipCount: number;
    maxResultCount: number;
  };
  purchaseLoading: boolean;
  purchaseError: string | null;
  purchaseResult: PurchaseTokenResultDto | null;
  tokenPrices: TokenPriceDto[];
  tokenPriceLoading: boolean;
  tokenPriceError: string | null;
  selectedAssetForPurchase: AssetDto | null;
}

// API Response interfaces
export interface ApiResponse<T> {
  result: T;
  targetUrl?: string;
  success: boolean;
  error?: any;
  unAuthorizedRequest: boolean;
  __abp?: boolean;
}

export interface PurchaseTokenDto {
  assetId: string;
  amount: number;
  paymentMethod: 'wallet' | 'card' | 'bank';
  walletAddress?: string;
}

export interface PurchaseTokenResultDto {
  success: boolean;
  transactionHash?: string;
  newBalance?: number;
  error?: string;
}

export interface TokenPriceDto {
  assetId: string;
  currentPrice: number;
  priceChange24h: number;
  marketCap: number;
  volume24h: number;
}

const initialState: AssetsState = {
  assets: [],
  userAssets: [],
  assetBalances: [],
  currentAsset: null,
  loading: false,
  error: null,
  createLoading: false,
  createError: null,
  deployLoading: false,
  deployError: null,
  verifyLoading: false,
  verifyError: null,
  getAllLoading: false,
  getAllError: null,
  pagination: {
    totalCount: 0,
    skipCount: 0,
    maxResultCount: 10
  },
  purchaseLoading: false,
  purchaseError: null,
  purchaseResult: null,
  tokenPrices: [],
  tokenPriceLoading: false,
  tokenPriceError: null,
  selectedAssetForPurchase: null,
};

// Thunks
export const createAsset = createAsyncThunk(
  "assets/createAsset",
  async (createData: CreateAssetDto, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        "api/services/app/Asset/CreateAssetAsync",
        createData
      );

      if (response.data.success) {
        return response.data.result;
      } else {
        return rejectWithValue({
          message: response.data.error?.message || "Failed to create asset",
          code: "ASSET_CREATION_FAILED"
        });
      }
    } catch (error: any) {
      return rejectWithValue({
        message: error.message || "An unknown error occurred",
        code: "UNKNOWN_ERROR"
      });
    }
  }
);

export const deployContract = createAsyncThunk(
  "assets/deployContract",
  async (assetId: string, { rejectWithValue }) => {
    try {
      const response = await axios.post<ApiResponse<DeployContractResultDto>>(
        `api/services/app/Asset/DeployContractAsync`,
        { assetId }
      );

      if (response.data.success) {
        return { assetId, ...response.data.result };
      } else {
        return rejectWithValue({
          message: response.data.error?.message || "Failed to deploy contract",
          code: "CONTRACT_DEPLOYMENT_FAILED"
        });
      }
    } catch (error: any) {
      return rejectWithValue({
        message: error.message || "An unknown error occurred",
        code: "UNKNOWN_ERROR"
      });
    }
  }
);

export const verifyContract = createAsyncThunk(
  "assets/verifyContract",
  async (assetId: string, { rejectWithValue }) => {
    try {
      const response = await axios.post<ApiResponse<boolean>>(
        `api/services/app/Asset/VerifyContractAsync`,
        { assetId }
      );

      if (response.data.success) {
        return { assetId, isVerified: response.data.result };
      } else {
        return rejectWithValue({
          message: response.data.error?.message || "Failed to verify contract",
          code: "CONTRACT_VERIFICATION_FAILED"
        });
      }
    } catch (error: any) {
      return rejectWithValue({
        message: error.message || "An unknown error occurred",
        code: "UNKNOWN_ERROR"
      });
    }
  }
);

export const getUsersAssetBalance = createAsyncThunk(
  "assets/getUsersAssetBalance",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get<ApiResponse<AssetBalanceDto[]>>(
        "api/services/app/Asset/GetUsersAssetBalance"
      );

      if (response.data.success) {
        return response.data.result;
      } else {
        return rejectWithValue({
          message: response.data.error?.message || "Failed to fetch asset balances",
          code: "BALANCE_FETCH_FAILED"
        });
      }
    } catch (error: any) {
      return rejectWithValue({
        message: error.message || "An unknown error occurred",
        code: "UNKNOWN_ERROR"
      });
    }
  }
);

export const getAssetById = createAsyncThunk(
  "assets/getAssetById",
  async (assetId: string, { rejectWithValue }) => {
    try {
      const response = await axios.get<ApiResponse<AssetDto>>(
        `api/services/app/Asset/Get`,
        { params: { id: assetId } }
      );

      if (response.data.success) {
        return response.data.result;
      } else {
        return rejectWithValue({
          message: response.data.error?.message || "Failed to fetch asset",
          code: "ASSET_FETCH_FAILED"
        });
      }
    } catch (error: any) {
      return rejectWithValue({
        message: error.message || "An unknown error occurred",
        code: "UNKNOWN_ERROR"
      });
    }
  }
);

// GET ALL mutation - This is the requested getAll function
export const getAllAssets = createAsyncThunk(
  "assets/getAllAssets",
  async (params: PagedAssetResultRequestDto, { rejectWithValue }) => {
    try {
      const response = await axios.get<ApiResponse<PagedResultDto<AssetDto>>>(
        "api/services/app/Asset/GetAll",
        { params }
      );

      if (response.data.success) {
        return {
          items: response.data.result.items,
          totalCount: response.data.result.totalCount
        };
      } else {
        return rejectWithValue({
          message: response.data.error?.message || "Failed to fetch assets",
          code: "ASSETS_FETCH_FAILED"
        });
      }
    } catch (error: any) {
      return rejectWithValue({
        message: error.message || "An unknown error occurred",
        code: "UNKNOWN_ERROR"
      });
    }
  }
);

export const getUserAssets = createAsyncThunk(
  "assets/getUserAssets",
  async (params: PagedAssetResultRequestDto, { rejectWithValue }) => {
    try {
      const response = await axios.get<ApiResponse<PagedResultDto<AssetDto>>>(
        "api/services/app/Asset/GetUserAssets",
        { params }
      );

      if (response.data.success) {
        return {
          items: response.data.result.items,
          totalCount: response.data.result.totalCount
        };
      } else {
        return rejectWithValue({
          message: response.data.error?.message || "Failed to fetch user assets",
          code: "USER_ASSETS_FETCH_FAILED"
        });
      }
    } catch (error: any) {
      return rejectWithValue({
        message: error.message || "An unknown error occurred",
        code: "UNKNOWN_ERROR"
      });
    }
  }
);

export const purchaseTokens = createAsyncThunk(
  "assets/purchaseTokens",
  async (purchaseData: PurchaseTokenDto, { rejectWithValue }) => {
    try {
      const response = await axios.post<ApiResponse<PurchaseTokenResultDto>>(
        "api/services/app/Asset/PurchaseTokensAsync",
        purchaseData
      );

      if (response.data.success) {
        return response.data.result;
      } else {
        return rejectWithValue({
          message: response.data.error?.message || "Failed to purchase tokens",
          code: "TOKEN_PURCHASE_FAILED"
        });
      }
    } catch (error: any) {
      return rejectWithValue({
        message: error.message || "An unknown error occurred",
        code: "UNKNOWN_ERROR"
      });
    }
  }
);

export const getTokenPrices = createAsyncThunk(
  "assets/getTokenPrices",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get<ApiResponse<any>>(
        "api/services/app/Asset/GetTokenPrices"
      );

      if (response.data.success) {
        return response.data.result.items;
      } else {
        return rejectWithValue({
          message: response.data.error?.message || "Failed to fetch token prices",
          code: "PRICES_FETCH_FAILED"
        });
      }
    } catch (error: any) {
      return rejectWithValue({
        message: error.message || "An unknown error occurred",
        code: "UNKNOWN_ERROR"
      });
    }
  }
);

export const getAssetPrice = createAsyncThunk(
  "assets/getAssetPrice",
  async (assetId: string, { rejectWithValue }) => {
    try {
      const response = await axios.get<ApiResponse<TokenPriceDto>>(
        `api/services/app/Asset/GetAssetPrice`,
        { params: { assetId } }
      );

      if (response.data.success) {
        return response.data.result;
      } else {
        return rejectWithValue({
          message: response.data.error?.message || "Failed to fetch asset price",
          code: "ASSET_PRICE_FETCH_FAILED"
        });
      }
    } catch (error: any) {
      return rejectWithValue({
        message: error.message || "An unknown error occurred",
        code: "UNKNOWN_ERROR"
      });
    }
  }
);


// Slice
const assetsSlice = createSlice({
  name: 'assets',
  initialState,
  reducers: {
    clearErrors: (state) => {
      state.error = null;
      state.createError = null;
      state.deployError = null;
      state.verifyError = null;
      state.getAllError = null;
    },
    clearCurrentAsset: (state) => {
      state.currentAsset = null;
    },
    clearAssets: (state) => {
      state.assets = [];
      state.userAssets = [];
      state.assetBalances = [];
      state.pagination.totalCount = 0;
      state.pagination.skipCount = 0;
    },
    updateAssetStatus: (state, action: PayloadAction<{ assetId: string, status: AssetStatus }>) => {
      const { assetId, status } = action.payload;
      
      // Update in assets array
      const assetIndex = state.assets.findIndex(asset => asset.id === assetId);
      if (assetIndex !== -1) {
        state.assets[assetIndex].status = status;
      }
      
      // Update in userAssets array
      const userAssetIndex = state.userAssets.findIndex(asset => asset.id === assetId);
      if (userAssetIndex !== -1) {
        state.userAssets[userAssetIndex].status = status;
      }
      
      // Update currentAsset if it matches
      if (state.currentAsset && state.currentAsset.id === assetId) {
        state.currentAsset.status = status;
      }
    },
    setPagination: (state, action: PayloadAction<{ skipCount: number, maxResultCount: number }>) => {
      state.pagination.skipCount = action.payload.skipCount;
      state.pagination.maxResultCount = action.payload.maxResultCount;
    },
     setSelectedAssetForPurchase: (state, action: PayloadAction<AssetDto | null>) => {
      state.selectedAssetForPurchase = action.payload;
    },
    clearPurchaseResult: (state) => {
      state.purchaseResult = null;
      state.purchaseError = null;
    },
  },
  extraReducers: (builder) => {
    // Create Asset
    builder
      .addCase(createAsset.pending, (state) => {
        state.createLoading = true;
        state.createError = null;
      })
      .addCase(createAsset.fulfilled, (state, action) => {
        state.createLoading = false;
        state.assets.unshift(action.payload);
        state.userAssets.unshift(action.payload);
      })
      .addCase(createAsset.rejected, (state, action) => {
        state.createLoading = false;
        state.createError = (action.payload as any)?.message || 'Failed to create asset';
      });

    // Deploy Contract
    builder
      .addCase(deployContract.pending, (state) => {
        state.deployLoading = true;
        state.deployError = null;
      })
      .addCase(deployContract.fulfilled, (state, action) => {
        state.deployLoading = false;
        const { assetId, contractAddress } = action.payload;
        
        // Update asset status to Minting
        const updateStatus = { assetId, status: AssetStatus.Minting };
        assetsSlice.caseReducers.updateAssetStatus(state, {
          type: 'assets/updateAssetStatus',
          payload: updateStatus
        });
        
        // Update contract address if available
        if (state.currentAsset && state.currentAsset.id === assetId) {
          state.currentAsset.contractAddress = contractAddress;
        }
      })
      .addCase(deployContract.rejected, (state, action) => {
        state.deployLoading = false;
        state.deployError = (action.payload as any)?.message || 'Failed to deploy contract';
      });

    // Verify Contract
    builder
      .addCase(verifyContract.pending, (state) => {
        state.verifyLoading = true;
        state.verifyError = null;
      })
      .addCase(verifyContract.fulfilled, (state, action) => {
        state.verifyLoading = false;
        const { assetId, isVerified } = action.payload;
        
        if (isVerified) {
          // Update asset status to Verified
          const updateStatus = { assetId, status: AssetStatus.Verified };
          assetsSlice.caseReducers.updateAssetStatus(state, {
            type: 'assets/updateAssetStatus',
            payload: updateStatus
          });
          
          // Update verification info
          if (state.currentAsset && state.currentAsset.id === assetId) {
            state.currentAsset.isVerified = true;
            state.currentAsset.verificationDate = new Date().toISOString();
          }
        }
      })
      .addCase(verifyContract.rejected, (state, action) => {
        state.verifyLoading = false;
        state.verifyError = (action.payload as any)?.message || 'Failed to verify contract';
      });

    // Get User's Asset Balance
    builder
      .addCase(getUsersAssetBalance.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getUsersAssetBalance.fulfilled, (state, action) => {
        state.loading = false;
        state.assetBalances = action.payload;
      })
      .addCase(getUsersAssetBalance.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as any)?.message || 'Failed to fetch asset balances';
      });

    // Get Asset by ID
    builder
      .addCase(getAssetById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAssetById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentAsset = action.payload;
      })
      .addCase(getAssetById.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as any)?.message || 'Failed to fetch asset';
      });

    // GET ALL Assets (mutation)
    builder
      .addCase(getAllAssets.pending, (state) => {
        state.getAllLoading = true;
        state.getAllError = null;
      })
      .addCase(getAllAssets.fulfilled, (state, action) => {
        state.getAllLoading = false;
        state.assets = action.payload.items;
        state.pagination.totalCount = action.payload.totalCount;
      })
      .addCase(getAllAssets.rejected, (state, action) => {
        state.getAllLoading = false;
        state.getAllError = (action.payload as any)?.message || 'Failed to fetch assets';
      });

    // Get User Assets
    builder
      .addCase(getUserAssets.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getUserAssets.fulfilled, (state, action) => {
        state.loading = false;
        state.userAssets = action.payload.items;
      })
      .addCase(getUserAssets.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as any)?.message || 'Failed to fetch user assets';
      });

       builder
      .addCase(purchaseTokens.pending, (state) => {
        state.purchaseLoading = true;
        state.purchaseError = null;
        state.purchaseResult = null;
      })
      .addCase(purchaseTokens.fulfilled, (state, action) => {
        state.purchaseLoading = false;
        state.purchaseResult = action.payload;
      })
      .addCase(purchaseTokens.rejected, (state, action) => {
        state.purchaseLoading = false;
        state.purchaseError = (action.payload as any)?.message || 'Failed to purchase tokens';
      });

    // Get Token Prices
    builder
      .addCase(getTokenPrices.pending, (state) => {
        state.tokenPriceLoading = true;
        state.tokenPriceError = null;
      })
      .addCase(getTokenPrices.fulfilled, (state, action) => {
        state.tokenPriceLoading = false;
        state.tokenPrices = action.payload;
      })
      .addCase(getTokenPrices.rejected, (state, action) => {
        state.tokenPriceLoading = false;
        state.tokenPriceError = (action.payload as any)?.message || 'Failed to fetch token prices';
      });

    // Get Asset Price
    builder
      .addCase(getAssetPrice.pending, (state) => {
        state.tokenPriceLoading = true;
        state.tokenPriceError = null;
      })
      .addCase(getAssetPrice.fulfilled, (state, action) => {
        state.tokenPriceLoading = false;
        // Update or add the price to tokenPrices array
        const index = state.tokenPrices.findIndex(p => p.assetId === action.payload.assetId);
        if (index !== -1) {
          state.tokenPrices[index] = action.payload;
        } else {
          state.tokenPrices.push(action.payload);
        }
      })
      .addCase(getAssetPrice.rejected, (state, action) => {
        state.tokenPriceLoading = false;
        state.tokenPriceError = (action.payload as any)?.message || 'Failed to fetch asset price';
      });

  },
});

export const {
  clearErrors,
  clearCurrentAsset,
  clearAssets,
  updateAssetStatus,
  setPagination,
  setSelectedAssetForPurchase,
  clearPurchaseResult 
} = assetsSlice.actions;

export default assetsSlice.reducer;